import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Headphones, AlertCircle, Volume2, Plus } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import AudioPlayer from './AudioPlayer';

interface PlaceData {
  Name: string;
  City: string;
  Country: string;
  Description_FR: string;
  Description_EN: string;
  AudioFile_FR: string | null;
  AudioFile_EN: string | null;
  Image: string | null;
  Latitude: number;
  Longitude: number;
}

interface PlaceAudioSectionProps {
  place: PlaceData;
  language: 'FR' | 'EN';
  onOpenPodcastGenerator?: (locationName: string, language: 'fr' | 'en') => void;
}

export const PlaceAudioSection: React.FC<PlaceAudioSectionProps> = ({ place, language, onOpenPodcastGenerator }) => {
  const [activePlayer, setActivePlayer] = useState<PlaceData | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<'FR' | 'EN'>('FR');
  const { t } = useTranslation();

  const hasAudio = place.AudioFile_FR || place.AudioFile_EN;

  if (!hasAudio) {
    return (
      <Card className="p-6 bg-gradient-to-br from-muted/20 to-muted/10 border-dashed">
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="font-semibold mb-2">{t('podcastComingSoon')}</h3>
          <p className="text-sm text-muted-foreground">
            {t('podcastComingSoonDesc')}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="overflow-hidden bg-card/90 backdrop-blur-sm border-border/50 shadow-lg">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-primary/10 rounded-xl">
              <Volume2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">{t('podcastAudio')}</h3>
              <p className="text-sm text-muted-foreground">
                {t('podcastAudioDesc')}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {/* Bouton Français - Disponible ou à créer */}
            {place.AudioFile_FR ? (
              <Button
                variant="ghost"
                className="w-full justify-between h-auto p-4 bg-muted/30 hover:bg-muted/50 border-0 transition-all duration-200 hover:shadow-md group"
                onClick={() => {
                  setSelectedLanguage('FR');
                  setActivePlayer(place);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500/10 rounded-lg group-hover:bg-blue-500/20 transition-colors">
                    <Play className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-foreground">Français</div>
                    <div className="text-xs text-muted-foreground">{t('frenchVersion')}</div>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-blue-500/10 text-blue-600 dark:text-blue-400 border-0">
                  FR
                </Badge>
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="w-full justify-between h-auto p-4 bg-muted/20 hover:bg-muted/30 border border-dashed border-muted-foreground/30 transition-all duration-200 hover:shadow-md group"
                onClick={() => {
                  if (onOpenPodcastGenerator) {
                    onOpenPodcastGenerator(place.Name, 'fr');
                  }
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-muted/50 rounded-lg group-hover:bg-muted/70 transition-colors">
                    <Plus className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-muted-foreground">Français</div>
                    <div className="text-xs text-muted-foreground">{t('podcastNotCreated')}</div>
                  </div>
                </div>
                <Badge variant="outline" className="border-muted-foreground/30 text-muted-foreground">
                  {t('createButton')}
                </Badge>
              </Button>
            )}

            {/* Bouton Anglais - Disponible ou à créer */}
            {place.AudioFile_EN ? (
              <Button
                variant="ghost"
                className="w-full justify-between h-auto p-4 bg-muted/30 hover:bg-muted/50 border-0 transition-all duration-200 hover:shadow-md group"
                onClick={() => {
                  setSelectedLanguage('EN');
                  setActivePlayer(place);
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500/10 rounded-lg group-hover:bg-emerald-500/20 transition-colors">
                    <Play className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-foreground">English</div>
                    <div className="text-xs text-muted-foreground">{t('englishVersion')}</div>
                  </div>
                </div>
                <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-0">
                  EN
                </Badge>
              </Button>
            ) : (
              <Button
                variant="ghost"
                className="w-full justify-between h-auto p-4 bg-muted/20 hover:bg-muted/30 border border-dashed border-muted-foreground/30 transition-all duration-200 hover:shadow-md group"
                onClick={() => {
                  if (onOpenPodcastGenerator) {
                    onOpenPodcastGenerator(place.Name, 'en');
                  }
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-muted/50 rounded-lg group-hover:bg-muted/70 transition-colors">
                    <Plus className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium text-muted-foreground">English</div>
                    <div className="text-xs text-muted-foreground">{t('podcastNotCreated')}</div>
                  </div>
                </div>
                <Badge variant="outline" className="border-muted-foreground/30 text-muted-foreground">
                  {t('createButton')}
                </Badge>
              </Button>
            )}
          </div>
        </div>
      </Card>

      {activePlayer && (
        <AudioPlayer
          podcast={activePlayer}
          language={selectedLanguage}
          onClose={() => setActivePlayer(null)}
        />
      )}
    </>
  );
};